import React, { useState, useEffect } from 'react';
import { brands as brandsApi } from '../../api/client';
import { RefreshCw, Download, Zap, TrendingUp, Info, Loader2, MessageSquare, FileText, Code } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

const StatCard = ({ label, value, change, positive, tooltip }) => (
  <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 md:p-6 hover:border-stone-400 dark:hover:border-stone-500 transition-colors">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium tracking-widest uppercase text-stone-500 dark:text-stone-400">{label}</span>
      <div className="relative group">
        <Info className="w-4 h-4 text-stone-400" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-stone-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
          {tooltip || label}
        </div>
      </div>
    </div>
    <div className="font-serif text-3xl md:text-4xl text-stone-900 dark:text-white mb-1">{value}</div>
    {change && (
      <span className={`text-sm flex items-center gap-1 ${positive ? "text-emerald-600" : "text-stone-500 dark:text-stone-400"}`}>
        {positive && <TrendingUp className="w-3 h-3" />}
        {change}
      </span>
    )}
  </div>
);

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

export default function OverviewPage({ activeBrandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');

  const loadData = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const result = await brandsApi.getStats(activeBrandId);
      setData(result);
    } catch (e) {
      console.error('Failed to load stats:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeBrandId]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  const { stats, visibilityTrend, sources, recentCitations } = data;

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Dashboard</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Visibility Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            {["7d", "30d", "90d"].map((range) => (
              <button key={range} onClick={() => setDateRange(range)}
                className={`px-3 py-1.5 text-sm ${dateRange === range ? 'bg-stone-900 text-white dark:bg-white dark:text-stone-900' : 'text-stone-600 dark:text-stone-400'}`}>
                {range}
              </button>
            ))}
          </div>
          <button onClick={loadData} className="px-3 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-50 flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard label="Visibility Score" value={stats.visibilityScore} change={`${stats.visibilityChange >= 0 ? '+' : ''}${stats.visibilityChange} this month`} positive={stats.visibilityChange > 0} tooltip="Overall visibility across all LLMs" />
        <StatCard label="Citations Won" value={stats.citationsWon} change={`+${stats.citationsChange} this week`} positive={stats.citationsChange > 0} tooltip="New citations gained" />
        <StatCard label="Actions" value={stats.actionsCompleted} change={`${stats.actionsPending} pending`} tooltip="Total actions executed" />
        <StatCard label="Competitor Gap" value={`${stats.competitorGap > 0 ? '+' : ''}${stats.competitorGap}`} change={stats.competitorGap > 0 ? 'Ahead' : 'Closing'} positive={stats.competitorGap > 0} tooltip="Points ahead of competitors" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        {/* Visibility Trend */}
        <div className="lg:col-span-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-xs font-medium tracking-widest uppercase block mb-1 text-stone-500">Trend</span>
              <h2 className="font-serif text-lg md:text-xl text-stone-800 dark:text-white">Visibility over time</h2>
            </div>
            <Badge variant="success"><TrendingUp className="w-3 h-3" /> +{stats.visibilityChange}%</Badge>
          </div>
          <div className="h-48 md:h-64" style={{ minHeight: '192px', minWidth: '200px' }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={192}>
              <AreaChart data={visibilityTrend}>
                <defs>
                  <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1c1917" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#1c1917" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 0 }} />
                <Area type="monotone" dataKey="score" stroke="#1c1917" fill="url(#colorScore)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* LLM Breakdown */}
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 md:p-6">
          <span className="text-xs font-medium tracking-widest uppercase block mb-1 text-stone-500">By Model</span>
          <h2 className="font-serif text-lg md:text-xl mb-6 text-stone-800 dark:text-white">LLM Coverage</h2>
          <div className="space-y-4">
            {[
              { name: "ChatGPT", key: "chatgpt", color: "bg-emerald-500" },
              { name: "Perplexity", key: "perplexity", color: "bg-violet-500" },
              { name: "Claude", key: "claude", color: "bg-amber-500" },
              { name: "Gemini", key: "gemini", color: "bg-blue-500" },
              { name: "Google AI", key: "google_aio", color: "bg-rose-500" },
            ].map((llm) => {
              const lastTrend = visibilityTrend?.[visibilityTrend.length - 1];
              const score = lastTrend?.[llm.key] || 0;
              return (
                <div key={llm.key}>
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-stone-700 dark:text-stone-300">{llm.name}</span>
                    <span className="text-stone-500">{Math.max(0, Math.min(100, score))}%</span>
                  </div>
                  <div className="h-2 bg-stone-100 dark:bg-stone-700 overflow-hidden">
                    <div className={`h-full ${llm.color} transition-all`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Citations */}
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-xs font-medium tracking-widest uppercase block mb-1 text-stone-500">Wins</span>
              <h2 className="font-serif text-lg md:text-xl text-stone-800 dark:text-white">Recent citations</h2>
            </div>
            <Badge variant="success">+{stats.citationsChange} this week</Badge>
          </div>
          <div className="divide-y divide-stone-100 dark:divide-stone-700">
            {recentCitations?.length > 0 ? recentCitations.map((c, i) => (
              <div key={i} className="py-4 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-stone-900 dark:text-white truncate">"{c.query}"</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-stone-500">{c.llm}</span>
                      <span className="text-stone-300 dark:text-stone-600">&bull;</span>
                      <span className="text-sm text-stone-500">{c.won_at ? new Date(c.won_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                  <Badge variant="success">#{c.position}</Badge>
                </div>
              </div>
            )) : (
              <p className="text-sm text-stone-500 py-4">No citations yet. Keep optimizing!</p>
            )}
          </div>
        </div>

        {/* Source Breakdown */}
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 md:p-6">
          <span className="text-xs font-medium tracking-widest uppercase block mb-1 text-stone-500">Sources</span>
          <h2 className="font-serif text-lg md:text-xl mb-6 text-stone-800 dark:text-white">Where LLMs cite from</h2>
          <div className="space-y-4">
            {sources?.map((source, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-stone-700 dark:text-stone-300">{source.name}</span>
                  <span className="text-stone-500">{source.percentage}%</span>
                </div>
                <div className="h-2 bg-stone-100 dark:bg-stone-700 overflow-hidden">
                  <div className="h-full bg-stone-900 dark:bg-white transition-all" style={{ width: `${source.percentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
